import { EndUserPages, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { useContext } from 'react';
import useEndUserRoutes from '../routes/useEndUserRoutes';
import TenantContext from './context/TenantContext';
import useAuthFlow from './useAuthFlow';
import useIsomorphicRedirect from './useIsomorphicRedirect';
import useIsomorphicRedirectByUrl from './useIsomorphicRedirectByUrl';

/**
 * Hook that redirects to Login Page or to sso client if sso is enabled with the provided path as referer.
 * This supports redirecting server-side or client-side depending on how the page was accessed.
 *
 * @param shouldRedirect whether to redirect
 *
 * @author Agustin Sosa
 */
export default function useLoginRedirect(shouldRedirect = false): void {
  const { router, loading: routesLoading } = useEndUserRoutes();
  const { getSsoAuthUrl, loading: authLoading, ssoPropertiesData } = useAuthFlow();
  const tenant = useContext(TenantContext);
  const { multiAuthEnabled } = tenant.publicConfig;
  const { ssoProperties } = ssoPropertiesData?.community || {};
  const ssoEnabled = checkPolicy(ssoProperties?.ssoEnabled);
  const nextPage = router.asPath;
  const loading = routesLoading || authLoading;

  useIsomorphicRedirect(shouldRedirect && !loading && !ssoEnabled, {
    route: EndUserPages.LoginPage,
    params: null,
    query: {
      [EndUserQueryParams.NEXT_PAGE]: nextPage
    }
  });

  const finalUrl = getSsoAuthUrl(nextPage);
  const finalRoute = router.getRouteAndParamsByPath(finalUrl);

  // Redirect to SSO/Multi-Auth login page, use an internal redirect if the URL is a community page
  useIsomorphicRedirect(shouldRedirect && !loading && !!finalRoute?.route, finalRoute);

  // Redirect to SSO/Multi-Auth login page, use an external redirect if the URL is not a community page
  useIsomorphicRedirectByUrl(
    shouldRedirect &&
      !loading &&
      (ssoEnabled || multiAuthEnabled) &&
      !!finalUrl &&
      finalRoute?.route == null,
    finalUrl
  );
}
